/**
 * Zgloszenia bledow frontu do /api/diag/client-logs.
 *
 * Aplikacja nie ma ErrorBoundary (patrz komentarz w client/src/App.tsx), wiec
 * blad wyrzucony poza obsluga zdarzenia znikal bez sladu: uzytkownik widzial
 * pusty ekran albo wieczny spinner, a w logu serwera nie bylo NICZEGO, bo blad
 * wydarzyl sie w przegladarce. Ten modul jest jedynym kanalem, ktorym taki blad
 * dociera do maszyny, na ktorej ktos moze go przeczytac.
 *
 * TRZY WLASNOSCI, KTORYCH BRAK ZAMIENIA RAPORTOWANIE BLEDOW W AWARIE:
 *
 *   1. Sufit na strone. Blad w petli renderowania potrafi wystrzelic tysiace
 *      razy na sekunde - bez limitu klient DDoS-owalby wlasny serwer.
 *   2. Rozbrojenie po odmowie. Zamkniete okno diagnostyki odpowiada 404, brak
 *      sesji 401, konto bez roli administratora 403. Wszystkie trzy znacza "nie probuj dalej", a nie "ponow za chwile".
 *   3. Cisza przy wlasnej awarii. Nieudany fetch NIE moze wygenerowac kolejnego
 *      zgloszenia, bo to jest dokladnie definicja petli.
 */

// Przeniesione z /dev-tools/client-logs 19.08.2026. Cale /dev-tools jest teraz
// odciete od internetu, a ta jedna trasa musiala zostac dostepna dla
// przegladarki - inaczej bledy frontu z produkcji przestalyby dochodzic, i to
// po cichu, bo przy wlasnej awarii ten modul milczy z zalozenia.
const SCIEZKA = "/api/diag/client-logs";

/** Ile zgloszen wysylamy najwyzej z jednego zaladowania strony. */
const SUFIT_NA_STRONE = 20;

/** Ile czekamy na kolejne bledy, zanim wyslemy paczke. Serwer i tak przyjmuje wsad. */
const ZWLOKA_MS = 2_000;

/** Ten sam limit stoi po stronie serwera w src/services/clientLogReports.ts. */
const MAKS_W_PACZCE = 50;

/**
 * Przyciecie pol PO STRONIE KLIENTA, tymi samymi wartosciami co serwer.
 *
 * Serwer tez je przycina, ale dopiero PO odebraniu ciala, wiec nie chroni przed
 * niczym, co dzieje sie po drodze. A po drodze stoja dwa twarde limity: 64 KiB
 * na cialo zadania z keepalive (odrzucenie leci wtedy wyjatkiem, zanim
 * cokolwiek wyjdzie z przegladarki) i 256 kB parsera JSON na routerze
 * diagnostyki. Slad stosu ze zbundlowanego Reacta potrafi miec kilka kilobajtow,
 * wiec dwadziescia bledow z petli renderowania przekracza pierwszy z nich bez
 * trudu - i rozbraja raportowanie dokladnie w tej awarii, dla ktorej powstalo.
 */
const MAKS_WIADOMOSC = 2_000;
const MAKS_STOS = 8_000;

/**
 * Gorny prog ciala jednej paczki. Z zapasem ponizej 64 KiB, bo limit keepalive
 * liczy CALE zadanie, razem z naglowkami i ciasteczkiem sesji.
 */
const MAKS_CIALO_BAJTOW = 48 * 1024;

interface Wpis {
  poziom: "warn" | "error";
  wiadomosc: string;
  stos: string | null;
  url: string | null;
  kiedy: string;
}

let kolejka: Wpis[] = [];
let wyslanych = 0;
let rozbrojone = false;
let zainstalowane = false;
let timer: number | null = null;
/** Podpisy juz zakolejkowanych bledow - ten sam blad w petli ma isc raz. */
let widziane = new Set<string>();
/** Podpiete sluchacze - trzymane, zeby dalo sie je odpiac w tescie. */
let sluchacze: { cel: EventTarget; rodzaj: string; fn: EventListener }[] = [];

/** Ten sam znacznik obciecia, ktorego uzywa serwer - cichego ucinania nie ma nigdzie. */
function przytnij(tekst: string, maks: number): string {
  return tekst.length > maks ? `${tekst.slice(0, maks)}...[obciete]` : tekst;
}

/** Dlugosc w bajtach UTF-8. TextEncoder jest w kazdej przegladarce, ktora ma fetch. */
function bajty(tekst: string): number {
  return new TextEncoder().encode(tekst).length;
}

function podpis(w: Wpis): string {
  return `${w.poziom}|${w.wiadomosc}|${w.stos?.slice(0, 200) ?? ""}`;
}

/**
 * Wyjmuje z kolejki tyle, ile zmiesci sie w jednym ciele.
 *
 * PIERWSZY WPIS WCHODZI ZAWSZE, takze gdyby sam przekraczal prog. Inaczej
 * pojedynczy wielki wpis zablokowalby kolejke na stale i zadne kolejne
 * zgloszenie juz by nie wyszlo.
 */
function zbierzPaczke(): Wpis[] {
  const paczka: Wpis[] = [];
  // Zapas na opakowanie obiektu i przecinki miedzy elementami.
  let rozmiar = 32;

  while (kolejka.length > 0 && paczka.length < MAKS_W_PACZCE) {
    const koszt = bajty(JSON.stringify(kolejka[0])) + 1;
    if (paczka.length > 0 && rozmiar + koszt > MAKS_CIALO_BAJTOW) break;
    rozmiar += koszt;
    paczka.push(kolejka.shift() as Wpis);
  }
  return paczka;
}

function wyslij(): void {
  timer = null;
  if (rozbrojone || kolejka.length === 0) return;

  const paczka = zbierzPaczke();

  // keepalive, zeby paczka wyszla takze wtedy, gdy blad zabija strone i
  // uzytkownik zaraz ja zamyka.
  void fetch(SCIEZKA, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ wpisy: paczka }),
    keepalive: true,
  })
    .then((res) => {
      // 401 - nikt nie jest zalogowany. 403 - konto bez roli administratora (diagnostyka jest tylko dla niego).
      // 404 - okno diagnostyki jest zamkniete. Zadne z tych trzech nie zmieni sie samo w trakcie zycia tej strony.
      if (res.status === 401 || res.status === 403 || res.status === 404) {
        rozbrojone = true;
        kolejka = [];
        return;
      }
      if (kolejka.length > 0) zaplanuj();
    })
    .catch(() => {
      // Cisza jest tu jedyna poprawna reakcja. Zgloszenie nieudanego zgloszenia
      // jest bledem, ktory sam siebie zglasza.
      rozbrojone = true;
      kolejka = [];
    });
}

/** Wysyla natychmiast, bez czekania na zwloke, i kasuje zaplanowane wyslanie. */
function wypchnij(): void {
  if (timer !== null) window.clearTimeout(timer);
  wyslij();
}

function zaplanuj(): void {
  if (timer !== null || rozbrojone) return;
  timer = window.setTimeout(wyslij, ZWLOKA_MS);
}

function zglos(poziom: "warn" | "error", wiadomosc: string, stos: string | null): void {
  if (rozbrojone || wyslanych >= SUFIT_NA_STRONE) return;
  const w: Wpis = {
    poziom,
    wiadomosc: przytnij(wiadomosc || "(bez tresci)", MAKS_WIADOMOSC),
    stos: stos === null ? null : przytnij(stos, MAKS_STOS),
    url: window.location.href,
    kiedy: new Date().toISOString(),
  };
  const p = podpis(w);
  if (widziane.has(p)) return;
  widziane.add(p);
  wyslanych += 1;
  kolejka.push(w);
  zaplanuj();
}

function zBledu(powod: unknown): { wiadomosc: string; stos: string | null } {
  if (powod instanceof Error) return { wiadomosc: `${powod.name}: ${powod.message}`, stos: powod.stack ?? null };
  if (typeof powod === "string") return { wiadomosc: powod, stos: null };
  try {
    return { wiadomosc: JSON.stringify(powod) ?? String(powod), stos: null };
  } catch {
    // Cykliczna referencja w odrzuconej wartosci. String() zawsze cos zwroci.
    return { wiadomosc: String(powod), stos: null };
  }
}

/** Podpina sluchacza i zapamietuje go, zeby reset w tescie mogl go odpiac. */
function podepnij(cel: EventTarget, rodzaj: string, fn: EventListener): void {
  cel.addEventListener(rodzaj, fn);
  sluchacze.push({ cel, rodzaj, fn });
}

/**
 * Podpina globalne przechwytywanie bledow. Wolane raz, z main.tsx.
 *
 * Wywolane drugi raz nie robi nic - podwojne sluchacze wysylalyby kazdy blad
 * dwa razy i wyczerpywaly sufit o polowe szybciej.
 */
export function zainstalujRaportowanieBledow(): void {
  if (zainstalowane) return;
  zainstalowane = true;

  podepnij(window, "error", (e) => {
    const zdarzenie = e as ErrorEvent;
    const { wiadomosc, stos } =
      zdarzenie.error !== undefined && zdarzenie.error !== null
        ? zBledu(zdarzenie.error)
        : { wiadomosc: zdarzenie.message, stos: null };
    const miejsce = zdarzenie.filename
      ? ` (${zdarzenie.filename}:${zdarzenie.lineno}:${zdarzenie.colno})`
      : "";
    zglos("error", `${wiadomosc}${miejsce}`, stos);
  });

  podepnij(window, "unhandledrejection", (e) => {
    const { wiadomosc, stos } = zBledu((e as PromiseRejectionEvent).reason);
    // Odrzucona obietnica to warn, nie error: czesc z nich to zerwane zapytania
    // przy zamykaniu karty, ktore nie sa awaria niczego.
    zglos("warn", `Nieobsluzone odrzucenie: ${wiadomosc}`, stos);
  });

  // ZNIKAJACA STRONA WYPYCHA KOLEJKE NATYCHMIAST.
  //
  // Bez tego dwusekundowa zwloka gubi najczestszy przypadek ze wszystkich: blad
  // zostawia pusty ekran, uzytkownik po sekundzie zamyka karte, a timer nigdy
  // nie odpala. keepalive tego nie ratuje - dziala dopiero dla zadania JUZ
  // wystartowanego, a tutaj fetch nie zdazyl ruszyc.
  podepnij(document, "visibilitychange", () => {
    if (document.visibilityState === "hidden") wypchnij();
  });
  podepnij(window, "pagehide", () => {
    wypchnij();
  });
}

/** Wylacznie dla testow - czysci stan modulu miedzy przypadkami. */
export function zresetujRaportowanieBledow(): void {
  if (timer !== null) window.clearTimeout(timer);
  timer = null;
  // Sluchacze MUSZA zniknac razem ze stanem. jsdom trzyma jedno okno na caly
  // plik testowy, wiec zostawiony sluchacz zglaszalby kazdy blad drugi raz.
  for (const s of sluchacze) s.cel.removeEventListener(s.rodzaj, s.fn);
  sluchacze = [];
  kolejka = [];
  widziane = new Set<string>();
  wyslanych = 0;
  rozbrojone = false;
  zainstalowane = false;
}

/** Wylacznie dla testow - wypycha kolejke bez czekania na zwloke. */
export function wyslijTeraz(): void {
  wypchnij();
}
